import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IAvatarTrain, AvatarTrainStatus, ModelType,
  PaginationResult, PaginationQuery, AvatarTrainQuery, CreateAvatarTrainRequest
} from '@packages/global';
import { avatarTrainApi } from '../services/user/avatar.api';
import { useModels } from '../hooks/useModels';
import { useDialog } from '../hooks/useDialog';
import { useWebSocketContext } from '../services/websocket/WebSocketContext';
import { MessageType, TaskStatusChangedMessage, TaskType, TaskStatus } from '../services/websocket/types';
import Pagination from '../components/Pagination';
import AvatarTrainForm from '../components/Avatar/AvatarTrainForm';
import AvatarTrainCard from '../components/Avatar/AvatarTrainCard';

const DEFAULT_PAGINATION: PaginationResult = { page: 1, limit: 12, total: 0, totalPages: 0, sortBy: 'createdAt', sortOrder: 'desc' };

export default function AvatarTrainingsPage() {
  const { t } = useTranslation(['avatar', 'common']);
  const { alert, confirm } = useDialog();
  const { client, isConnected } = useWebSocketContext();

  const [trains, setTrains] = useState<IAvatarTrain[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<PaginationResult>(DEFAULT_PAGINATION);
  const [trainForm, setTrainForm] = useState<CreateAvatarTrainRequest>({ name: '', videoUrl: '', gender: 'female', modelId: '' });

  const { models: trainModels, loading: trainModelsLoading } = useModels(ModelType.AVATAR_TRAIN);

  useEffect(() => {
    if (trainModels.length > 0 && !trainForm.modelId) {
      setTrainForm(p => ({ ...p, modelId: trainModels[0].id }));
    }
  }, [trainModels]);

  const loadTrains = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await avatarTrainApi.getPageList({
        query: {},
        pagination: { page, limit: 12, sortBy: 'createdAt', sortOrder: 'desc' }
      } as PaginationQuery<AvatarTrainQuery>);
      setTrains(res.items);
      setPagination(p => ({ ...p, page, total: res.pagination.total, totalPages: res.pagination.totalPages }));
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTrains(); }, [loadTrains]);

  useEffect(() => {
    if (!client || !isConnected) return;
    const statusMap: Record<TaskStatus, string> = {
      [TaskStatus.PENDING]: 'pending', [TaskStatus.SUBMITTED]: 'submitted',
      [TaskStatus.PROCESSING]: 'processing', [TaskStatus.COMPLETED]: 'completed', [TaskStatus.FAILED]: 'failed'
    };
    const unsub = client.onMessage(MessageType.TASK_STATUS_CHANGED, (msg: TaskStatusChangedMessage) => {
      if (msg.taskType !== TaskType.AVATAR_TRAIN) return;
      setTrains(prev => prev.map(tr =>
        tr.id === msg.taskId ? { ...tr, status: statusMap[msg.newStatus] as AvatarTrainStatus } : tr
      ));
      if (msg.newStatus === TaskStatus.COMPLETED || msg.newStatus === TaskStatus.FAILED) {
        avatarTrainApi.getById(msg.taskId)
          .then(u => setTrains(prev => prev.map(tr => tr.id === u.id ? u : tr)))
          .catch(() => { });
      }
    });
    return unsub;
  }, [client, isConnected]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!trainForm.modelId) { await alert(t('avatar:train.selectModel'), '', 'warning'); return; }
    try {
      const created = await avatarTrainApi.create(trainForm);
      setTrains(prev => [created, ...prev]);
      setTrainForm({ name: '', videoUrl: '', gender: 'female', modelId: trainModels[0]?.id || '' });
    } catch (err: any) { await alert(err.message || t('avatar:train.createFailed'), '', 'danger'); }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!await confirm(t('avatar:train.deleteConfirm', { name }), t('avatar:train.deleteTitle'), 'danger')) return;
    try { await avatarTrainApi.delete(id); setTrains(prev => prev.filter(tr => tr.id !== id)); }
    catch { await alert(t('avatar:train.deleteFailed'), '', 'danger'); }
  };

  const handleRetrain = async (item: IAvatarTrain) => {
    try {
      const updated = await avatarTrainApi.retrain(item.id);
      setTrains(prev => prev.map(tr => tr.id === updated.id ? updated : tr));
    } catch (err: any) { await alert(err.message || t('avatar:train.createFailed'), '', 'danger'); }
  };

  return (
    <div className="flex h-screen bg-bg-page">
      <AvatarTrainForm
        trainForm={trainForm}
        trainModels={trainModels}
        trainModelsLoading={trainModelsLoading}
        onTrainFormChange={data => setTrainForm(p => ({ ...p, ...data }))}
        onTrainSubmit={handleSubmit}
      />

      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="mb-4">
            <h1 className="text-2xl font-semibold text-gray-50">{t('avatar:tabs.trains')}</h1>
            <p className="text-sm text-gray-400 mt-1">{t('avatar:train.subtitle')}</p>
          </div>

          {loading ? (
            <div className="bg-bg-surface rounded-xl border border-border-subtle p-12 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              <span className="ml-3 text-gray-400">{t('common:loading')}</span>
            </div>
          ) : trains.length === 0 ? (
            <div className="bg-bg-surface rounded-xl border border-border-subtle p-12 text-center text-gray-400">
              {t('avatar:train.empty')}
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
              {trains.map(item => (
                <AvatarTrainCard key={item.id} item={item} onDelete={handleDelete} onRetrain={handleRetrain} />
              ))}
            </div>
          )}

          {pagination.totalPages > 1 && (
            <div className="bg-bg-surface rounded-xl border border-border-subtle p-4 mt-6">
              <Pagination currentPage={pagination.page} totalPages={pagination.totalPages} onPageChange={loadTrains} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
